import { Menu, Search, X } from 'lucide-react'
import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Input } from '@/components/ui/input'
import { useMobileSidebar } from '@/contexts/MobileSidebarContext'
import { useTourAnchors } from '@/contexts/TourAnchorContext'
import { MOBILE_BREAKPOINT, useMediaQuery } from '@/hooks/use-mobile'
import { TOUR_ANCHORS } from '@/lib/productTour'
import { cn } from '@/lib/utils'

export type PageHeaderSearch = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  maxLength?: number
  className?: string
  inputClassName?: string
}

type PageHeaderProps = {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  topActions?: ReactNode
  bottomActions?: ReactNode
  search?: PageHeaderSearch
  className?: string
}

export function PageHeader({
  title,
  subtitle,
  actions,
  topActions,
  bottomActions,
  search,
  className,
}: PageHeaderProps) {
  const { setMobileOpen } = useMobileSidebar()
  const { register } = useTourAnchors()
  const rightTop = topActions ?? null
  const rightBottom = bottomActions ?? actions ?? null
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  // Defaults to desktop in jsdom (no matchMedia matches).
  const isDesktop = useMediaQuery(`(min-width: ${MOBILE_BREAKPOINT}px)`)

  const toggleMobileSearch = () => {
    setMobileSearchOpen((open) => {
      const next = !open
      if (next) {
        requestAnimationFrame(() => searchInputRef.current?.focus())
      }
      return next
    })
  }

  const titleEl = (
    <h1 className="text-[24px] leading-[34px] lg:text-[28px] font-semibold text-black font-['Visby_CF']">
      {title}
    </h1>
  )

  const subtitleEl = subtitle ? (
    <div className="text-[13px] leading-[22px] font-light text-[#333333] mt-[17px] font-['Poppins'] lg:font-normal lg:leading-[18px] lg:text-black lg:mt-1 lg:font-['Visby_CF']">
      {subtitle}
    </div>
  ) : null

  const searchInputEl = search ? (
    <div className={cn('relative w-full max-w-md', search.className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        ref={searchInputRef}
        placeholder={search.placeholder}
        aria-label={search.placeholder ?? 'Search'}
        value={search.value}
        maxLength={search.maxLength}
        onChange={(event) => search.onChange(event.target.value)}
        className={cn(
          'pl-9 pr-9 h-10 rounded-full bg-muted/50 border-transparent shadow-sm focus-visible:ring-2 focus-visible:ring-primary/20',
          search.inputClassName,
        )}
      />
      {search.value ? (
        <button
          type="button"
          onClick={() => search.onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      ) : null}
    </div>
  ) : null

  return (
    <div
      className={cn(
        'bg-white p-5 w-full max-w-full min-w-0 lg:border-b lg:border-border/60 lg:pt-9 lg:pb-5 lg:px-[45px]',
        className,
      )}
    >
      {isDesktop ? (
        <div className="flex flex-row items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {titleEl}
            {subtitleEl}
            {searchInputEl ? <div className="mt-3">{searchInputEl}</div> : null}
          </div>
          {rightTop || rightBottom ? (
            <div className="flex flex-col items-end gap-3">
              {rightTop ? <div className="flex items-center gap-1.5">{rightTop}</div> : null}
              {rightBottom ? <div className="flex items-center gap-2">{rightBottom}</div> : null}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
            <button
              // The founder product tour's fallback anchor below 1024px, where
              // the rail it would otherwise point at is translated off-canvas.
              ref={register(TOUR_ANCHORS.mobileNav)}
              onClick={() => setMobileOpen(true)}
              className="-ml-1.5 p-1.5 rounded-lg hover:bg-muted transition-colors"
              aria-label="Open navigation"
            >
              <Menu className="w-5 h-5" />
            </button>
            {(search || rightTop) && (
              <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-2">
                {search && (
                  <button
                    type="button"
                    onClick={toggleMobileSearch}
                    aria-label={mobileSearchOpen ? 'Close search' : 'Search'}
                    aria-expanded={mobileSearchOpen}
                    className="-mr-1 rounded-lg"
                  >
                    {mobileSearchOpen ? (
                      <X className="w-5 h-5" strokeWidth={2} />
                    ) : (
                      <Search className="w-5 h-5" strokeWidth={2} />
                    )}
                  </button>
                )}
                {rightTop}
              </div>
            )}
          </div>
          <div className="mt-7">
            {titleEl}
            {subtitleEl}
            {searchInputEl && mobileSearchOpen ? <div className="mt-3">{searchInputEl}</div> : null}
            {rightBottom ? (
              // Wrap on mobile: a wide action group (tag buttons, "New Folder" +
              // "Upload") would otherwise widen the header past the viewport and
              // scroll the whole page sideways.
              <div className="flex flex-wrap items-center justify-end gap-2 mt-3">{rightBottom}</div>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}
